import { useEffect, useState } from "react";
import API, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Receipt, Eye } from "@phosphor-icons/react";

export async function downloadPdf(path, filename) {
  const res = await API.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

export default function RevolutReceiptSettings({ initial, onSaved }) {
  const [form, setForm] = useState(initial || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(initial || {}); }, [initial]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try { const { data } = await API.put("/platform/revolut/receipt-settings", { ...form, vat_rate: Number(form.vat_rate) || 0 }); setForm(data); onSaved?.(data); toast.success("Receipt details saved"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't save receipt details"); }
    finally { setBusy(false); }
  };
  const preview = async () => {
    try { await downloadPdf("/platform/revolut/receipt-sample.pdf", "receipt-sample.pdf"); }
    catch { toast.error("Couldn't build the sample receipt"); }
  };
  return (
    <div className="mt-3 rounded-md border border-border p-3" data-testid="revolut-receipt-settings">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Receipt size={14} className="text-muted-foreground" />
        <p className="text-xs font-semibold flex-1 min-w-[200px]">Receipt details <span className="font-normal text-muted-foreground">— printed on every payment and refund receipt e-mailed to workspace owners</span></p>
        <Button variant="ghost" size="sm" className="text-xs h-7" onClick={preview} data-testid="revolut-receipt-preview-btn"><Eye size={12} className="mr-1" /> Preview sample</Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px]">Legal company name</Label>
          <Input value={form.legal_name || ""} onChange={set("legal_name")} placeholder="Fixlr Ltd" className="text-xs mt-1" data-testid="receipt-legal-name" />
        </div>
        <div>
          <Label className="text-[11px]">VAT number</Label>
          <Input value={form.vat_number || ""} onChange={set("vat_number")} placeholder="GB 123 4567 89" className="text-xs mt-1" data-testid="receipt-vat-number" />
        </div>
        <div>
          <Label className="text-[11px]">Registered address</Label>
          <Textarea value={form.address || ""} onChange={set("address")} rows={3} placeholder={"1 Example Street\nLondon\nEC1A 1AA"} className="text-xs mt-1" data-testid="receipt-address" />
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px]">VAT rate included in prices (%)</Label>
            <Input type="number" min={0} max={100} step={0.5} value={form.vat_rate ?? 0} onChange={set("vat_rate")} className="text-xs mt-1 w-32" data-testid="receipt-vat-rate" />
            <p className="text-[10px] text-muted-foreground mt-1">0 = no VAT line. 20 prints a net / VAT breakdown of the VAT-inclusive price.</p>
          </div>
          <div>
            <Label className="text-[11px]">Footer note</Label>
            <Input value={form.footer_note || ""} onChange={set("footer_note")} placeholder="Thank you for your business." className="text-xs mt-1" data-testid="receipt-footer-note" />
          </div>
        </div>
      </div>
      <div className="mt-3 flex justify-end"><Button size="sm" className="text-xs h-7" onClick={save} disabled={busy} data-testid="receipt-save-btn">{busy ? "Saving…" : "Save receipt details"}</Button></div>
    </div>
  );
}
